// src/features/owner/sections/system/TopicRefinerSection.tsx — the LLM topic
// refiner switch + counters (1.24.00 B4). DEFAULT OFF.
//
// State (Off/On), whether ANTHROPIC_API_KEY is configured, the model id, the
// last run (when / candidates / refined / failed / who triggered it), lifetime
// totals, posts currently tagged by the model, candidates waiting — and three
// controls: Turn on, Turn off, Run one batch now (how the owner tests it without
// waiting for the 5-minute beat). The sweep does nothing while the switch is off,
// even with the key present.
//
// Every write goes useOwnerAction → ownerService → POST /owner/actions/<slug>,
// each audited under its own slug; NEVER logAction (that is the client-only
// prototype log the Dashboard quick actions still use — the anti-pattern here).
// After each write the aggregate is refetched: the switch shows the server's
// truth, not an optimistic guess.
import { useState } from 'react';
import styled from 'styled-components';
import { useTopicRefiner } from '@/hooks/useTopicRefiner';
import { useOwnerAction, REFINER_NOT_CONFIGURED_MESSAGE } from '@/hooks/useOwnerAction';
import { Button, Badge } from '@/ui';
import { Icon } from '@/icons';
import { OwnerPanel, KpiCard, EmptyState } from '../../components';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Controls = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
`;

const Dl = styled.dl`
  margin: 0;
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 6px 16px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  dt { color: ${({ theme }) => theme.color.textMuted}; }
  dd { margin: 0; color: ${({ theme }) => theme.color.textPrimary}; font-family: ${({ theme }) => theme.font.mono}; }
`;

const Note = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  max-width: 70ch;
`;

const ErrorText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.danger};
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  white-space: pre-wrap;
`;

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function TopicRefinerSection() {
  const { data, loading, error, refetch } = useTopicRefiner();
  const action = useOwnerAction();
  const [busy, setBusy] = useState<'on' | 'off' | 'run' | null>(null);

  async function run(kind: 'on' | 'off' | 'run') {
    setBusy(kind);
    const ok =
      kind === 'on'
        ? await action.enableTopicRefiner()
        : kind === 'off'
          ? await action.disableTopicRefiner()
          : // The candidate count as it stood BEFORE the click: the run response
            // cannot distinguish "processed a batch" from "returned before
            // writing a row because nothing was waiting" — both answer ran=true
            // with the PREVIOUS run in `lastRun`. See topicRefinerRunMessage.
            await action.runTopicRefiner(data?.pendingCandidates ?? 0);
    setBusy(null);
    if (ok) refetch();
  }

  if (loading && !data) return <p style={{ opacity: 0.6 }}>Loading…</p>;
  if (error || !data) return <EmptyState icon="sparkles" message={error ?? 'No data'} />;

  const { enabled, keyConfigured, model, lastRun, totals, refinedPosts, pendingCandidates } = data;

  return (
    <Stack>
      <OwnerPanel
        title="Topic refiner"
        action={
          <StatusRow>
            <Badge tone={enabled ? 'cyan' : 'neutral'}>{enabled ? 'On' : 'Off'}</Badge>
            <Badge tone={keyConfigured ? 'cyan' : 'amber'}>
              {keyConfigured ? 'Key configured' : 'No key'}
            </Badge>
          </StatusRow>
        }
      >
        <Stack>
          <Note>
            A small model re-reads posts the keyword classifier filed under General and files them
            under one of the eight topics. It only ranks For You: it never hides a post, never
            touches badges, and does nothing at all while the switch is off. Turning it on spends
            money per batch.
          </Note>
          {!keyConfigured && (
            <Note role="status" data-testid="refiner-not-configured">{REFINER_NOT_CONFIGURED_MESSAGE}</Note>
          )}
          <Controls>
            {enabled ? (
              <Button variant="danger" onClick={() => void run('off')} disabled={busy !== null}>
                <Icon name="ban" size={14} /> {busy === 'off' ? 'Turning off…' : 'Turn off'}
              </Button>
            ) : (
              <Button variant="primary" onClick={() => void run('on')} disabled={busy !== null}>
                <Icon name="sparkles" size={14} /> {busy === 'on' ? 'Turning on…' : 'Turn on'}
              </Button>
            )}
            <Button variant="secondary" onClick={() => void run('run')} disabled={busy !== null}>
              <Icon name="play" size={14} /> {busy === 'run' ? 'Running…' : 'Run one batch now'}
            </Button>
            <Button variant="ghost" onClick={refetch} disabled={busy !== null}>
              <Icon name="refresh" size={14} /> Refresh
            </Button>
          </Controls>
          <Dl>
            <dt>Model</dt>
            <dd>{model || 'Not set'}</dd>
          </Dl>
        </Stack>
      </OwnerPanel>

      <KpiGrid>
        <KpiCard icon="file-text" tone="brand" label="Posts tagged by the model" value={refinedPosts} deltaPct={null} trend="flat" />
        <KpiCard icon="hourglass" tone="ember" label="Candidates waiting" value={pendingCandidates} deltaPct={null} trend="flat" />
        <KpiCard icon="activity" tone="teal" label="Runs (lifetime)" value={totals.runs} deltaPct={null} trend="flat" />
        <KpiCard icon="check" tone="mint" label="Refined (lifetime)" value={totals.refined} deltaPct={null} trend="flat" />
        <KpiCard icon="ban" tone="magenta" label="Failed (lifetime)" value={totals.failed} deltaPct={null} trend="flat" />
      </KpiGrid>

      <OwnerPanel title="Last run">
        {lastRun ? (
          <Stack>
            <Dl>
              <dt>Started</dt>
              <dd>{formatWhen(lastRun.startedAt)}</dd>
              <dt>Triggered by</dt>
              <dd>{lastRun.triggeredBy === 'manual' ? 'manual (console)' : 'beat (every 5 min)'}</dd>
              <dt>Candidates</dt>
              <dd>{lastRun.candidates}</dd>
              <dt>Refined</dt>
              <dd>{lastRun.refined}</dd>
              <dt>Failed</dt>
              <dd>{lastRun.failed}</dd>
            </Dl>
            {lastRun.error && <ErrorText role="status">{lastRun.error}</ErrorText>}
          </Stack>
        ) : (
          <EmptyState icon="clock" message="No run yet. A row is written only when a batch had candidates or failed." />
        )}
      </OwnerPanel>
    </Stack>
  );
}
